<template>
  <div class="archives f-h">
    <div class="archives__content f-h">
      <Problems
        v-if="page === 0"
        archives
      >
        <Switcher
          slot="filters-shortcut"
          v-model="page"
          class="archives__content__switcher"
          :titles="pages"
        />
      </Problems>
      <Pending
        v-else-if="page === 1"
        archives
      >
        <Switcher
          slot="filters-shortcut"
          v-model="page"
          class="archives__content__switcher"
          :titles="pages"
        />
      </Pending>

      <div v-else-if="page === 2 || page === 3" class="f-h">
        <FiltersZone :modal-title="$t('message.OPTIONS')">
          <div>
            <RadioGroup v-model="sortBy" :items="sortItems" sortable />
          </div>
          <Switcher
            slot="shortcuts"
            v-model="page"
            class="archives__content__switcher"
            :titles="pages"
          />
        </FiltersZone>
        <div class="f-h-below-filters archives__content__wrapper">
          <RefreshOnscrollList ref="refList" class="f-h" @scrollBottom="refresh(true)">
            <div v-if="page == 2">
              <ArchivesMeetingRow
                v-for="meeting in sortedItems"
                :key="meeting.id"
                :meeting="meeting"
                @chipClick="onChipClick"
              />
            </div>
            <div v-else>
              <InformationItem
                v-for="info in sortedItems"
                :key="info.id"
                :value="info"
                :min="$store.state.device.isSmartphonePortrait"
                disabled
                @chipClick="onChipClick($event, info)"
              />
            </div>
          </RefreshOnscrollList>
        </div>
      </div>
    </div>
    <FileViewer
      v-if="fileViewerVisible && fileViewerTarget"
      ref="fileViewer"
      :value="fileViewerTarget.files || []"
      :index="fileViewerIndex"
      pathname="informations/"
      @close="fileViewerVisible = false"
    />
  </div>
</template>

<script>
/**
 * Archives page.
 * Allow switching between problems, actions, infos and meetings archives
 */
import { cloneDeep, isEqual } from 'lodash';
// Vuex
import { FETCH_TEAMS } from '@/store/action-types';
// Components
import ArchivesMeetingRow from '@/components/archives-meeting-row.vue';
import FileViewer from '@/components/file-system/file-viewer.vue';
import FiltersZone from '@/components/filters-zone.vue';
import InformationItem from '@/components/information-item.vue';
import Pending from '@/views/dmp/pending.vue';
import Problems from '@/views/dmp/problems.vue';
import RadioGroup from '@/components/radio-group.vue';
import RefreshOnscrollList from '@/components/refresh-onscroll-list.vue';
import Switcher from '@/components/switcher.vue';

const REQ_ITEMS_COUNT = 20;

export default {

  name: 'Archives',

  components: {
    ArchivesMeetingRow,
    FileViewer,
    FiltersZone,
    InformationItem,
    Pending,
    Problems,
    RadioGroup,
    RefreshOnscrollList,
    Switcher,
  },

  data() {
    return {
      // Sorting
      deadlineSortItem: { id: 2, label: this.$t('message.SORT_BY_DEADLINE') },
      sortItems: [
        { id: 0, label: this.$t('message.SORT_BY_TITLE') },
        { id: 1, label: this.$t('message.SORT_BY_ENTRY_DATE') },
      ],
      itemsLimitOffset: 0,
      sqlLimit: REQ_ITEMS_COUNT,
      // Data
      items: [],
      meetings: [],
      infos: [],
      refreshInProgress: false,
      // File viewer
      fileViewerTarget: null,
      fileViewerVisible: false,
      fileViewerIndex: 0,
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.ARCHIVES'),
    };
  },

  computed: {
    titleKey() {
      return this.page === 2 ? 'name' : 'text';
    },
    sortedItems() {
      const { desc } = this.sortBy;
      const { titleKey } = this;
      if (this.items) {
        const items = cloneDeep(this.items);
        if (items.length) {
          switch (this.sortBy.value) {
            case 0: return !desc
              ? items.sort((a, b) => (a[titleKey] || '').localeCompare(b[titleKey]))
              : items.sort((a, b) => (b[titleKey] || '').localeCompare(a[titleKey]));
            case 1: return !desc
              ? items.sort((a, b) => a.createdSortable - b.createdSortable)
              : items.sort((a, b) => b.createdSortable - a.createdSortable);
            case 2: return !desc
              ? items.sort((a, b) => a.deadlineSortable - b.deadlineSortable)
              : items.sort((a, b) => b.deadlineSortable - a.deadlineSortable);
            default: return items;
          }
        }
      }
      return [];
    },
    /** Array: Available pages (problems, actions and meetings) */
    pages() {
      const { isSmartphoneLandscape } = this.$store.state.device;
      return [
        this.$t(isSmartphoneLandscape ? 'message.PROBLEMS_SHORT' : 'message.PROBLEMS'),
        this.$t('message.ACTIONS'),
        this.$t(isSmartphoneLandscape ? 'message.MEETINGS_SHORT' : 'message.MEETINGS'),
        this.$t(isSmartphoneLandscape ? 'message.INFORMATIONS_SHORT' : 'message.INFORMATIONS'),
      ];
    },
    /** Number: Index of the page currently displayed */
    page: {
      get() {
        return Number(this.$route.query.page || 0);
      },
      set(newVal) {
        const query = Object.assign({}, { ...this.$route.query, page: String(newVal) });
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Object: Sorting options for infos and meetings */
    sortBy: {
      get() {
        const value = this.$route.query.archivesSortBy === undefined ? 1 : Number(this.$route.query.archivesSortBy);
        return { value, desc: !(this.$route.query.archivesSortDesc === 'false') };
      },
      set(sortOpt) {
        const query = Object.assign({}, this.$route.query);
        query.archivesSortBy = String(sortOpt.value);
        query.archivesSortDesc = sortOpt.desc ? 'true' : 'false';
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  watch: {
    page() {
      this.$nextTick(this.setupPage);
    },
    sortBy(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.$nextTick(this.setupPage);
      }
    },
  },

  created() {
    this.$store.dispatch(FETCH_TEAMS);
  },

  mounted() {
    this.setupPage();
  },

  methods: {
    refresh(increaseLimit) {
      const { page } = this;
      if ([0, 1].includes(page)) {
        return;
      }
      if (this.refreshInProgress) {
        setTimeout(() => {
          this.refresh(increaseLimit);
        }, 100);
        return;
      }
      this.refreshInProgress = true;
      // URL according to page switcher
      let url = '/server/meetings/get.php';
      let array = 'meetings';
      const method = this.$http.post;
      if (page === 3) {
        url = '/server/information/get.php';
        array = 'infos';
      }
      // Refresh page, reset offset
      if (increaseLimit !== true) {
        this.sqlLimit = this.itemsLimitOffset || REQ_ITEMS_COUNT;
        this.itemsLimitOffset = 0;
        if (this.$refs.refList) {
          this.$refs.refList.scrollTop();
        }
      }
      const params = {
        archived: true,
        sortBy: {
          index: this.sortBy.value,
          desc: !!this.sortBy.desc,
        },
        limit: {
          offset: this.itemsLimitOffset,
          count: this.sqlLimit,
        },
      };

      method(url, params).then(({ data }) => {
        if (typeof data === 'object') {
          if (page === 3) {
            // Prevent having duplicates since sentInfos and normal infos are merged
            const prevIds = this.items.map(i => i.id);
            const newIds = (data.infos || []).map(i => i.id).concat(prevIds);
            data = (data.infos || [])
              .filter(i => !prevIds.includes(i.id))
              .concat((data.sentInfos || []).filter(i => !newIds.includes(i.id)));
          }
          this[array] = increaseLimit === true ? this[array].concat(data) : data;
          this.items = this[array];
          this.itemsLimitOffset += data.length;
          this.sqlLimit = REQ_ITEMS_COUNT;
          if (data.length > 0 && this.$refs.refList) {
            this.$nextTick(() => {
              setTimeout(this.$refs.refList.checkHeight, 500);
            });
          }
        }
        this.refreshInProgress = false;
      });
    },
    /** Value change on switcher button */
    setupPage() {
      // Used for information items
      if (this.page === 2) {
        if (this.sortBy.value >= 2) {
          this.sortBy = { ...this.sortBy, value: 1 };
        }
        this.sortItems.splice(this.deadlineSortItem.id, 1);
      } else if (this.sortItems.length === 2) {
        this.sortItems.push(this.deadlineSortItem);
      }
      if (this.$refs.refList) {
        this.$refs.refList.scrollTop();
      }
      this.items = [];
      this.itemsLimitOffset = 0;
      this.$nextTick(this.refresh);
    },
    /** Triggered when a file chip is clicked */
    onChipClick(event, info) {
      let index;
      if (event.file) {
        index = info.files.indexOf(event.file.id);
      }
      this.openFileViewer(info, index);
    },
    /**
     * Open file viewer to view file attachments of an info
     * @param {Object} info Info object
     * @param {Number} index - Index of the file to display first
     */
    openFileViewer(info, index) {
      this.fileViewerTarget = info;
      if (!this.fileViewerTarget) {
        return;
      }
      this.fileViewerIndex = index;
      this.fileViewerVisible = true;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.archives {

  &__content {
    &__switcher {
      height: 2rem;

      .switcher__element {
        padding: 0.5rem 1rem;
      }
    }

    &__wrapper {
      &.f-h-below-filters {
        padding: 0px;
      }

      .refresh-onscroll-list {
        padding: 0px $page-content-padding;
      }
    }
  }

  .information-item {
    padding: 0px 15px;
  }
}

@media (max-width: $smartphone-landscape) {
  .archives {
    .information-item {
      padding-left: 10px;
      padding-right: 10px;
    }
  }
}

</style>
